import { createStore, SetState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { i18nStore } from '~/modules/stores/i18nStore';

export interface ConfirmOptions {
    cancelButtonLabel?: string;
    confirmButtonLabel?: string;
    previousFocusedElement: HTMLElement | null;
}

export enum ConfirmStatus {
    OPENED = 'OPENED',
    CANCELED = 'CANCELED',
    CONFIRMED = 'CONFIRMED',
}

export interface ConfirmState extends State {
    confirmTitle: string;
    confirmText: string;
    cancelButtonLabel: string;
    confirmButtonLabel: string;
    confirmStatus: ConfirmStatus;
    previousFocusedElement: HTMLElement | null;
}

export const initialState: ConfirmState = {
    confirmTitle: '',
    confirmText: '',
    cancelButtonLabel: '',
    confirmButtonLabel: '',
    confirmStatus: ConfirmStatus.CANCELED,
    previousFocusedElement: null,
};

export function selectStatus(state: ConfirmState): ConfirmStatus {
    return state.confirmStatus;
}

export const confirmStore = createStore(
    initialState,
    (set: SetState<ConfirmState>) => ({
        // @ts-expect-error * Automated Type Error Fix
        open(confirmTitle, confirmText, options: ConfirmOptions) {
            set(function open(draft: ConfirmState) {
                draft.confirmTitle = confirmTitle;
                draft.confirmText = confirmText;
                if (options) {
                    const { cancelButtonLabel, confirmButtonLabel, previousFocusedElement } = options;
                    draft.cancelButtonLabel = cancelButtonLabel ?? i18nStore.getState().i18nStaticCache.shared.cancel;
                    draft.confirmButtonLabel = confirmButtonLabel ?? i18nStore.getState().i18nStaticCache.shared.ok;
                    draft.previousFocusedElement = previousFocusedElement ?? null;
                }
                draft.confirmStatus = ConfirmStatus.OPENED;
            });
        },
        cancel() {
            set(function cancel(draft: ConfirmState) {
                draft.confirmTitle = '';
                draft.confirmText = '';
                draft.confirmStatus = ConfirmStatus.CANCELED;
            });
        },
        confirm() {
            set(function confirm(draft: ConfirmState) {
                draft.confirmStatus = ConfirmStatus.CONFIRMED;
            });
        },
        reset() {
            set(function reset() {
                return initialState;
            });
        },
    }),
    'confirmStore',
    IS_DEVELOPMENT,
);

confirmStore.subscribe((confirmStatus: ConfirmStatus) => {
    // Get the current style attribute of the document body, or an empty string if it doesn't exist
    const bodyStyle = document.body.getAttribute('style') || '';

    // If the confirm modal it's OPENED, add 'overflow-y: hidden' to the style attribute, otherwise, remove it
    const updatedStyle =
        confirmStatus === ConfirmStatus.OPENED
            ? `${bodyStyle} overflow-y: hidden;`
            : bodyStyle.replace(' overflow-y: hidden;', '');
    document.body.setAttribute('style', updatedStyle);
}, selectStatus);
